import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { isAdminEmail } from "../../lib/adminService";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);

      // Authentication succeeded, but the admin panel shares its Firebase
      // user pool with the mobile app. Only allow users in the `admins`
      // allowlist; sign everyone else back out.
      const allowed = await isAdminEmail(credential.user.email);
      if (!allowed) {
        await signOut(auth);
        toast({
          variant: "destructive",
          title: "Access denied",
          description: "This account is not authorized to access the admin panel.",
        });
        return;
      }

      navigate("/admin/blogs");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Invalid email or password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand-beige relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('/patterns/mandala-pattern.svg')] opacity-5 bg-repeat" />

      <div className="relative w-full max-w-md px-4 animate-gentle-fade">
        <Card className="w-full backdrop-blur-sm bg-white/90 shadow-lg rounded-2xl p-8 border border-forest-green/10">
          {/* Logo or Brand Mark */}
          <div className="mb-8 text-center">
            <img
              src="/cittasya-logo.svg"
              alt="Cittasya"
              className="h-12 mx-auto mb-2"
            />
            <h2 className="font-heading text-2xl text-forest-green">
              Admin Portal
            </h2>
            <p className="text-sm text-copper-brown/60 font-body mt-1">
              Sign in to manage your content
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-forest-green"
              >
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="admin@cittasya.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-white/50 border-misty-sage focus:border-forest-green focus:ring-forest-green/20 placeholder:text-forest-green/30 text-forest-green"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-medium text-forest-green"
              >
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-white/50 border-misty-sage focus:border-forest-green focus:ring-forest-green/20 placeholder:text-forest-green/30 text-forest-green"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-forest-green hover:bg-forest-green/90 text-white transition-colors"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
